import { useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  VALIDATION_TYPE_AIDES,
  VALIDATION_TYPE_LABELS,
  type DiscrepancyRow,
  type ValidationType,
} from '@kn/shared';
import { api } from '../../api/client';
import { messageErreur } from '../../api/error-message';

/** Longueur minimale du commentaire, alignée sur la validation zod partagée. */
const COMMENTAIRE_MINIMUM = 10;

function formaterNombre(valeur: string): string {
  const nombre = Number(valeur);
  if (!Number.isFinite(nombre)) return valeur;
  return nombre.toLocaleString('fr-FR', { maximumFractionDigits: 3 });
}

/**
 * Instruction d'un écart : validation temporaire ou définitive, avec
 * commentaire obligatoire (règle 14), et proposition de neutralisation
 * pré-remplie quand des mouvements de casse expliquent l'écart.
 */
export function ValidationEcart({
  ecart,
  peutValider,
  onFerme,
}: {
  ecart: DiscrepancyRow;
  peutValider: boolean;
  /** Absent quand la validation occupe une page : il n'y a rien a refermer. */
  onFerme?: () => void;
}): ReactElement {
  const queryClient = useQueryClient();
  const [type, setType] = useState<ValidationType>('TEMPORAIRE');
  const [quantite, setQuantite] = useState(ecart.ecartResiduel);
  const [commentaire, setCommentaire] = useState('');
  const [motif, setMotif] = useState<string | undefined>(undefined);
  const [erreur, setErreur] = useState<string | null>(null);

  const proposition = useQuery({
    queryKey: ['discrepancies', 'proposition', ecart.id],
    queryFn: () => api.neutralisationProposal(ecart.id),
  });

  // Une validation définitive constate un solde : elle ne porte pas de
  // quantité, on n'en synchronise donc pas l'état — on la dérive.
  const quantiteEffective = type === 'DEFINITIVE' ? '0' : quantite;

  const validation = useMutation({
    mutationFn: () =>
      api.validerEcart(ecart.id, {
        type,
        quantite: quantiteEffective,
        commentaire,
        ...(motif ? { motif } : {}),
        ...(motif && proposition.data ? { mouvements: proposition.data.mouvements } : {}),
      }),
    onSuccess: () => {
      setErreur(null);
      void queryClient.invalidateQueries({ queryKey: ['discrepancies'] });
      onFerme?.();
    },
    onError: (error: unknown) => setErreur(messageErreur(error)),
  });

  const annulation = useMutation({
    mutationFn: (params: { id: string; motif: string }) =>
      api.annulerValidation(params.id, params.motif),
    onSuccess: () => {
      setErreur(null);
      void queryClient.invalidateQueries({ queryKey: ['discrepancies'] });
    },
    onError: (error: unknown) => setErreur(messageErreur(error)),
  });

  const commentaireTropCourt = commentaire.trim().length < COMMENTAIRE_MINIMUM;
  const actives = ecart.validations.filter((v) => v.statut === 'ACTIVE');

  return (
    <div className="carte">
      <h2>
        Instruire l&apos;écart{' '}
        {ecart.maille === 'FAMILLE' ? ecart.codpros.join(' / ') : ecart.cle} &mdash;{' '}
        {ecart.libelle}
      </h2>
      <p className="sous-titre">
        {ecart.maille === 'FAMILLE' && (
          <>
            Validation posée au niveau <strong>famille</strong> : elle couvre les{' '}
            {ecart.codpros.length} codes membres.{' '}
          </>
        )}
        Écart {formaterNombre(ecart.ecartUvc)} UVC, apparu le {ecart.apparuLe}. Déjà validé :{' '}
        {formaterNombre(ecart.quantiteValidee)} UVC. Reste à instruire :{' '}
        <strong>{formaterNombre(ecart.ecartResiduel)}</strong> UVC.
      </p>

      {proposition.data && (
        <div className="alerte alerte--info">
          <strong>Neutralisation proposée</strong> ({proposition.data.couverture.toLowerCase()}){' '}
          &mdash; {proposition.data.commentaire}
          <ul className="liste-cles">
            {proposition.data.mouvements.map((mouvement) => (
              <li key={`${mouvement.jour}-${mouvement.refMvt}`}>
                {mouvement.jour} · {formaterNombre(mouvement.quantite)} UVC ·{' '}
                {mouvement.refMvt ?? '—'}
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="bouton bouton--secondaire"
            onClick={() => {
              const p = proposition.data;
              if (!p) return;
              setType('TEMPORAIRE');
              setQuantite(p.quantite);
              setCommentaire(p.commentaire);
              setMotif(p.motif);
            }}
          >
            Pré-remplir depuis la proposition
          </button>
        </div>
      )}

      {peutValider ? (
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            if (commentaireTropCourt) {
              setErreur(
                `Le commentaire est obligatoire et doit expliquer l'écart (${COMMENTAIRE_MINIMUM} caractères au minimum).`,
              );
              return;
            }
            validation.mutate();
          }}
        >
          <div className="barre-actions">
            <label className="champ" htmlFor="validation-type">
              <span>Niveau de validation</span>
              <select
                id="validation-type"
                value={type}
                onChange={(event) => setType(event.target.value as ValidationType)}
              >
                <option value="TEMPORAIRE">{VALIDATION_TYPE_LABELS.TEMPORAIRE}</option>
                <option value="DEFINITIVE">{VALIDATION_TYPE_LABELS.DEFINITIVE}</option>
              </select>
            </label>
            <label className="champ" htmlFor="validation-quantite">
              <span>Quantité (UVC)</span>
              <input
                id="validation-quantite"
                type="text"
                value={quantiteEffective}
                disabled={type === 'DEFINITIVE'}
                onChange={(event) => setQuantite(event.target.value)}
              />
            </label>
          </div>

          <p className="sous-titre">{VALIDATION_TYPE_AIDES[type]}</p>

          <label className="champ" htmlFor="validation-commentaire">
            <span>Commentaire (obligatoire)</span>
            <textarea
              id="validation-commentaire"
              rows={3}
              value={commentaire}
              placeholder="Ce qui explique l'écart, et ce qu'on attend pour le solder"
              onChange={(event) => setCommentaire(event.target.value)}
            />
          </label>

          {erreur && <p className="alerte alerte--erreur">{erreur}</p>}

          <div className="barre-actions">
            <button type="submit" className="bouton" disabled={validation.isPending}>
              {validation.isPending ? 'Enregistrement…' : 'Valider'}
            </button>
            {onFerme && (
              <button type="button" className="bouton bouton--secondaire" onClick={onFerme}>
                Fermer
              </button>
            )}
          </div>
        </form>
      ) : (
        <p className="sous-titre">Seul un opérateur peut valider un écart.</p>
      )}

      {ecart.validations.length > 0 && (
        <>
          <h3>Validations</h3>
          <table className="tableau">
            <thead>
              <tr>
                <th scope="col">Niveau</th>
                <th scope="col">Quantité</th>
                <th scope="col">Commentaire</th>
                <th scope="col">Auteur</th>
                <th scope="col">Statut</th>
                {peutValider && <th scope="col">Action</th>}
              </tr>
            </thead>
            <tbody>
              {ecart.validations.map((v) => (
                <tr key={v.id}>
                  <td>{VALIDATION_TYPE_LABELS[v.type]}</td>
                  <td className="droite">{formaterNombre(v.quantite)}</td>
                  <td>
                    {v.commentaire}
                    {v.motifAnnulation && (
                      <div className="sous-titre">Annulée : {v.motifAnnulation}</div>
                    )}
                  </td>
                  <td>{v.userEmail ?? '—'}</td>
                  <td>
                    <span
                      className={
                        v.statut === 'ACTIVE'
                          ? 'etiquette etiquette--actif'
                          : 'etiquette etiquette--inactif'
                      }
                    >
                      {v.statut === 'ACTIVE' ? 'Active' : 'Annulée'}
                    </span>
                  </td>
                  {peutValider && (
                    <td>
                      {v.statut === 'ACTIVE' && (
                        <button
                          type="button"
                          className="bouton bouton--lien"
                          onClick={() => {
                            const raison = window.prompt(
                              "Motif d'annulation (obligatoire, 10 caractères au minimum) :",
                            );
                            if (raison && raison.trim().length >= COMMENTAIRE_MINIMUM) {
                              annulation.mutate({ id: v.id, motif: raison.trim() });
                            }
                          }}
                        >
                          Annuler
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="sous-titre">
            {actives.length} validation(s) active(s). Rien n&apos;est supprimé : une validation
            annulée reste tracée avec son motif.
          </p>
        </>
      )}
    </div>
  );
}
